import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import type { UserNotification } from "@/lib/types";

export function useNotifications() {
  const { user } = useAuth();
  const { club } = useClub();
  const [notifications, setNotifications] = useState<(UserNotification & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("[useNotifications] mounting", { uid: user?.uid, clubId: club?.id });
    if (!user || !club) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "users", user.uid, "notifications"),
      where("clubId", "==", club.id),
      orderBy("createdAt", "desc"),
      limit(100)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        console.log("[useNotifications] snapshot", { count: snap.size });
        setNotifications(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as UserNotification) }))
        );
        setLoading(false);
      },
      (err) => {
        console.error("[useNotifications] error", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user?.uid, club?.id]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Keep iOS app icon badge in sync with unread count
  useEffect(() => {
    import("@/lib/pushNotifications").then((m) => m.setAppBadge(unreadCount)).catch(() => {});
  }, [unreadCount]);

  const markRead = async (id: string) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, "users", user.uid, "notifications", id), { read: true });
    } catch {}
  };

  const markAllRead = async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => batch.update(doc(db, "users", user.uid, "notifications", n.id), { read: true }));
      await batch.commit();
    } catch {}
  };

  return { notifications, unreadCount, loading, markRead, markAllRead };
}
